/**
 * Business Logic Layer - Model Service
 * Contains business rules and orchestrates data operations
 */

import { Model, ModelStats, ModelFormData } from '../models/Model';
import { ModelRepository } from '../repositories/ModelRepository';

export class ModelService {
  private repository: ModelRepository;

  constructor() {
    this.repository = ModelRepository.getInstance();
  }

  /**
   * Get all models
   */
  public async getAllModels(): Promise<Model[]> {
    return await this.repository.getAllModels();
  }

  /**
   * Get model by ID
   */
  public async getModelById(id: string): Promise<Model | null> {
    return await this.repository.getModelById(id);
  }

  /**
   * Create new model from form data
   */
  public async createModel(formData: ModelFormData): Promise<Model> {
    // Business logic: validate before creating
    this.validateModelData(formData);

    return await this.repository.createModel({
      ...formData,
    });
  }

  /**
   * Update existing model
   */
  public async updateModel(id: string, formData: Partial<ModelFormData>): Promise<Model | null> {
    // Business logic: validate before updating
    if (formData.model_name || formData.provider || formData.description) {
      this.validateModelData(formData as ModelFormData);
    }

    return await this.repository.updateModel(id, formData);
  }

  /**
   * Delete model
   */
  public async deleteModel(id: string): Promise<boolean> {
    return await this.repository.deleteModel(id);
  }

  /**
   * Deploy a model (change status to deployed)
   */
  public async deployModel(id: string): Promise<Model | null> {
    const model = await this.repository.getModelById(id);
    
    if (!model) {
      throw new Error('Model not found');
    }

    if (model.status === 'deployed') {
      throw new Error('Model is already deployed');
    }

    return await this.repository.updateModel(id, { status: 'deployed' });
  }

  /**
   * Search models
   */
  public async searchModels(query: string): Promise<Model[]> {
    if (!query.trim()) {
      return await this.getAllModels();
    }
    return await this.repository.searchModels(query);
  }

  /**
   * Calculate statistics from models
   */
  public calculateStats(models: Model[]): ModelStats {
    return {
      total: models.length,
      deployed: models.filter(m => m.status === 'deployed').length,
      ready: models.filter(m => m.status === 'ready').length,
      featured: models.filter(m => m.featured).length,
    };
  }

  /**
   * Get recent models (last 5)
   */
  public async getRecentModels(limit: number = 5): Promise<Model[]> {
    const models = await this.getAllModels();
    return models.slice(0, limit);
  }

  /**
   * Get featured models
   */
  public async getFeaturedModels(): Promise<Model[]> {
    const models = await this.getAllModels();
    return models.filter(m => m.featured);
  }

  /**
   * Validate model data
   */
  private validateModelData(data: Partial<ModelFormData>): void {
    if (data.model_name && !data.model_name.trim()) {
      throw new Error('Model name is required');
    }
    if (data.version && !data.version.trim()) {
      throw new Error('Model version is required');
    }
    if (data.provider && !data.provider.trim()) {
      throw new Error('Model provider is required');
    }
    if (data.description && !data.description.trim()) {
      throw new Error('Model description is required');
    }
  }
}

// Export singleton instance
export const modelService = new ModelService();
